import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Plane, Hotel, Train, Bus, Car, User, CreditCard, CheckCircle, MapPin, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api, formatApiErrorDetail } from '../lib/api';
import { openRazorpayCheckout } from '../lib/razorpay';
import { track, Events } from '../lib/analytics';
import SavedTravellerPicker from './account/SavedTravellerPicker';
import CheckoutGatewaySelector from './checkout/CheckoutGatewaySelector';

const TYPE_TO_COLLECTION = {
  hotels: 'hotels', trains: 'trains', buses: 'buses', cabs: 'cabs',
  villas: 'villas', packages: 'packages', tours: 'tours', visa: 'visas', visas: 'visas',
  cruise: 'cruises', cruises: 'cruises', forex: 'forex', insurance: 'insurance',
};

const BookingDetails = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [bookingItem, setBookingItem] = useState(null);
  const [v2Breakdown, setV2Breakdown] = useState(null); // for flights
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    passengers: 1,
  });
  const [selectedGateway, setSelectedGateway] = useState(null);

  // Flights are loaded via V2 — pull from router state (set by SearchResults).
  // For all other services use the legacy v1 catalog.
  useEffect(() => {
    if (type === 'flights') {
      const stateItem = location.state?.item;
      if (stateItem) {
        setBookingItem(stateItem);
        setV2Breakdown(stateItem.price_breakdown);
        setLoading(false);
        return;
      }
      // No state — user landed directly. Reprice as a fallback to get pricing.
      api
        .post('/v2/flights/review', { fare_id: id, adults: 1, tier: 'b2c' })
        .then((r) => {
          setBookingItem({ fare_id: id, ...r.data });
          setV2Breakdown(r.data.price_breakdown);
        })
        .catch(() => setBookingItem(null))
        .finally(() => setLoading(false));
      return;
    }
    if (type === 'hotels') {
      const stateItem = location.state?.item;
      if (stateItem) {
        setBookingItem(stateItem);
        setLoading(false);
        return;
      }
    }
    const collection = TYPE_TO_COLLECTION[type];
    if (!collection) {
      setLoading(false);
      return;
    }
    api
      .get(`/services/${collection}/${id}`)
      .then((r) => setBookingItem(r.data))
      .catch(() => setBookingItem(null))
      .finally(() => setLoading(false));
  }, [type, id, location.state]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // V2 + Razorpay path: flights & hotels (extend to other modules as live providers come online).
      if (type === 'flights' || type === 'hotels') {
        const passengers = parseInt(formData.passengers) || 1;
        const [first, ...rest] = formData.name.trim().split(' ');
        const paxList = type === 'hotels'
          ? [{ title: 'Mr', first_name: first || 'Guest', last_name: rest.join(' ') || 'User' }]
          : Array.from({ length: passengers }, (_, i) => ({
              title: 'Mr',
              first_name: i === 0 ? (first || 'Guest') : `Passenger${i + 1}`,
              last_name: i === 0 ? (rest.join(' ') || 'User') : 'User',
            }));

        // Step 1: create booking + Razorpay order on the backend
        const created = await api.post('/v2/bookings/create', {
          service: type,
          item_id: id,
          passengers: paxList,
          contact: { name: formData.name, email: formData.email, phone: formData.phone },
          units: type === 'hotels' ? 1 : passengers,
          tier: 'b2c',
          item_snapshot: type === 'hotels' ? bookingItem : undefined,
        });

        // Step 2: open Razorpay Standard Checkout — real payment modal
        track(Events.PAYMENT_INITIATED, { booking_id: created.data.id, amount: created.data.total_amount, service: type });
        let payment;
        try {
          payment = await openRazorpayCheckout({
            orderId: created.data.razorpay_order_id,
            amount: Math.round(created.data.total_amount * 100), // paise
            keyId: created.data.razorpay_key_id,
            description: `${type === 'flights' ? 'Flight' : 'Hotel'} booking ${created.data.id}`,
            prefill: {
              name: formData.name,
              email: formData.email,
              contact: formData.phone,
            },
          });
        } catch (rzpErr) {
          // User dismissed or payment failed — keep the pending booking record, surface a toast.
          toast({
            title: rzpErr.code === 'CANCELLED' ? 'Payment cancelled' : 'Payment failed',
            description: rzpErr.code === 'CANCELLED'
              ? 'You can retry payment from your bookings list.'
              : (rzpErr.message || 'Try a different payment method.'),
            variant: 'destructive',
          });
          return;
        }

        // Step 3: verify signature + issue ticket / hotel voucher
        const confirmed = await api.post('/v2/bookings/confirm', {
          booking_id: created.data.id,
          razorpay_order_id: payment.razorpay_order_id,
          razorpay_payment_id: payment.razorpay_payment_id,
          razorpay_signature: payment.razorpay_signature,
        });
        track(Events.PAYMENT_COMPLETED, { booking_id: created.data.id, amount: created.data.total_amount });
        track(Events.BOOKING_CONFIRMED, { booking_id: created.data.id, service: type, amount: created.data.total_amount });

        toast({
          title: 'Booking Confirmed!',
          description: type === 'flights'
            ? `PNR: ${confirmed.data.provider_confirmation?.pnr || confirmed.data.id}. Total ₹${confirmed.data.total_amount}`
            : `Voucher: ${confirmed.data.provider_confirmation?.voucher || confirmed.data.id}. Total ₹${confirmed.data.total_amount}`,
        });
      } else {
        // Legacy mock flow for modules we haven't migrated yet
        const res = await api.post('/bookings', {
          type,
          item_id: id,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          passengers: parseInt(formData.passengers) || 1,
        });
        toast({
          title: 'Booking Confirmed!',
          description: `Your booking ID is ${res.data.id}. Check your email for details.`,
        });
      }
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      toast({
        title: 'Booking Failed',
        description: formatApiErrorDetail(err.response?.data?.detail) || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getIcon = () => {
    const iconMap = {
      flights: <Plane className="h-6 w-6" />,
      hotels: <Hotel className="h-6 w-6" />,
      trains: <Train className="h-6 w-6" />,
      buses: <Bus className="h-6 w-6" />,
      cabs: <Car className="h-6 w-6" />,
    };
    return iconMap[type];
  };

  const calculateTotal = () => {
    if (!bookingItem) return 0;
    if (type === 'flights' && v2Breakdown) {
      // Recompute breakdown locally if passenger count changes
      const passengers = parseInt(formData.passengers) || 1;
      if (passengers === v2Breakdown.units) return v2Breakdown.total;
      const netPerUnit = v2Breakdown.net_per_unit;
      const base = netPerUnit * passengers;
      const markup = base * (v2Breakdown.markup_percent / 100);
      const fee = v2Breakdown.convenience_fee_per_unit * passengers;
      return Math.round((base + markup + fee) * 100) / 100;
    }
    const basePrice = bookingItem.display_price ?? bookingItem.price ?? bookingItem.net_price_per_night ?? 0;
    const passengers = parseInt(formData.passengers) || 1;
    return type === 'cabs' ? basePrice * 50 : (type === 'hotels' ? basePrice : basePrice * passengers);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!bookingItem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-300">Booking item not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white dark:from-gray-900 dark:to-gray-900 transition-colors duration-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-3 rounded-lg">{getIcon()}</div>
            <div>
              <h1 className="text-3xl font-bold">Complete Your Booking</h1>
              <p className="text-red-100">Fill in your details to confirm</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-gray-900 dark:text-white">
                  <User className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <span>Traveler Details</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                  <SavedTravellerPicker onPick={(t) => setFormData((f) => ({
                    ...f,
                    name: t.full_name,
                    dob: t.dob,
                    gender: t.gender,
                    passport_number: t.passport_number,
                    passport_expiry: t.passport_expiry,
                    nationality: t.nationality,
                  }))} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 dark:text-gray-300">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="input-phone"
                        placeholder="+91 9876543210"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    {type !== 'hotels' && type !== 'cabs' && (
                      <div>
                        <Label htmlFor="passengers" className="text-gray-700 dark:text-gray-300">Number of {type === 'flights' || type === 'trains' ? 'Passengers' : 'Seats'} *</Label>
                        <Input
                          id="passengers"
                          name="passengers"
                          type="number"
                          min="1"
                          max="10"
                          data-testid="input-passengers"
                          value={formData.passengers}
                          onChange={handleInputChange}
                          required
                          className="mt-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                        />
                      </div>
                    )}
                  </div>

                  <Separator className="my-6 dark:bg-gray-700" />

                  <div>
                    <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2 text-gray-900 dark:text-white">
                      <CreditCard className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <span>Payment Information</span>
                    </h3>
                    <CheckoutGatewaySelector
                      value={selectedGateway}
                      onChange={setSelectedGateway}
                      fallback={(
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 inline mr-2" />
                            Your payment is secured with 256-bit SSL via Razorpay
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Cards · UPI · Netbanking · Wallets — all major Indian payment methods supported.
                          </p>
                        </div>
                      )}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="confirm-booking-btn"
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg transition-colors duration-200 disabled:opacity-70"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : `Confirm Booking - ₹${calculateTotal().toLocaleString()}`}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg sticky top-24">
              <CardHeader>
                <CardTitle className="text-gray-900 dark:text-white">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(type === 'flights' || type === 'trains' || type === 'buses') && (
                  <>
                    <div className="flex items-center gap-3">
                      {type === 'flights' && bookingItem.airline_code && (
                        <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-1.5 rounded-lg w-12 h-12 flex items-center justify-center shadow-sm flex-shrink-0">
                          <img
                            src={`https://images.kiwi.com/airlines/64/${bookingItem.airline_code}.png`}
                            alt={bookingItem.airline}
                            className="max-h-9 max-w-9 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                          {bookingItem.airline || bookingItem.trainName || bookingItem.operator}
                        </h3>
                        {(bookingItem.flight_number || bookingItem.flightNumber) && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{bookingItem.flight_number || bookingItem.flightNumber}</p>
                        )}
                        {bookingItem.trainNumber && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">Train No: {bookingItem.trainNumber}</p>
                        )}
                        {bookingItem.busType && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{bookingItem.busType}</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg space-y-3">
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 dark:text-white">{bookingItem.from || bookingItem.from_code}</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{bookingItem.departure}</p>
                        </div>
                      </div>
                      <div className="border-l-2 border-dashed border-gray-300 dark:border-gray-600 ml-2 h-8"></div>
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 dark:text-white">{bookingItem.to || bookingItem.to_code}</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{bookingItem.arrival}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Duration</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{bookingItem.duration}</span>
                    </div>
                  </>
                )}

                {type === 'hotels' && (
                  <>
                    <div>
                      <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">{bookingItem.name}</h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                        <MapPin className="h-4 w-4" />
                        <span>{bookingItem.area}{bookingItem.area && bookingItem.city ? ', ' : ''}{bookingItem.city || bookingItem.location}</span>
                      </div>
                    </div>
                    {bookingItem.image && <img src={bookingItem.image} alt={bookingItem.name} className="w-full h-32 object-cover rounded-lg" />}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Room Type</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{bookingItem.room_type || bookingItem.roomType || 'Standard'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Rating</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{bookingItem.rating || '—'} ⭐</span>
                      </div>
                    </div>
                  </>
                )}

                {type === 'cabs' && (
                  <>
                    <div>
                      <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">{bookingItem.model}</h3>
                      <Badge className="bg-red-600 text-white">{bookingItem.type}</Badge>
                    </div>
                    <img src={bookingItem.image} alt={bookingItem.model} className="w-full h-32 object-cover rounded-lg" />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Capacity</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{bookingItem.capacity} Seater</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Rate</span>
                        <span className="font-semibold text-gray-900 dark:text-white">₹{bookingItem.price}/{bookingItem.priceUnit}</span>
                      </div>
                    </div>
                  </>
                )}

                <Separator className="dark:bg-gray-700" />

                {/* Price Breakdown */}
                {type === 'flights' && v2Breakdown ? (
                  <div className="space-y-2" data-testid="price-breakdown-v2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Base Fare ({formData.passengers} × ₹{v2Breakdown.net_per_unit.toLocaleString()})</span>
                      <span className="font-semibold text-gray-900 dark:text-white">₹{(v2Breakdown.net_per_unit * (parseInt(formData.passengers) || 1)).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Service Charge ({v2Breakdown.markup_percent}%)</span>
                      <span className="font-semibold text-gray-900 dark:text-white">₹{(v2Breakdown.net_per_unit * (parseInt(formData.passengers) || 1) * v2Breakdown.markup_percent / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Convenience Fee (₹{v2Breakdown.convenience_fee_per_unit} × {formData.passengers})</span>
                      <span className="font-semibold text-gray-900 dark:text-white">₹{(v2Breakdown.convenience_fee_per_unit * (parseInt(formData.passengers) || 1)).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Taxes</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">Included</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Base Price</span>
                      <span className="font-semibold text-gray-900 dark:text-white">₹{(bookingItem.price || 0).toLocaleString()}</span>
                    </div>
                    {type !== 'hotels' && type !== 'cabs' && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">{formData.passengers}x Passenger(s)</span>
                        <span className="font-semibold text-gray-900 dark:text-white">₹{((bookingItem.price || 0) * formData.passengers).toLocaleString()}</span>
                      </div>
                    )}
                    {type === 'cabs' && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Est. Distance (50km)</span>
                        <span className="font-semibold text-gray-900 dark:text-white">₹{((bookingItem.price || 0) * 50).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Taxes & Fees</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">Included</span>
                    </div>
                  </div>
                )}

                <Separator className="dark:bg-gray-700" />

                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg text-gray-900 dark:text-white">Total Amount</span>
                  <span className="font-bold text-2xl text-red-600 dark:text-red-400">₹{calculateTotal().toLocaleString()}</span>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-sm text-green-800 dark:text-green-300 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Free Cancellation up to 24 hours before
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
